import {
  Banknote,
  Calendar,
  CalendarDays,
  TrendingUp,
  Check,
  type LucideIcon,
} from 'lucide-react';
import { useT, type StringKey } from '@lib/i18n';
import { formatOMR, type PriceBreakdown } from '@lib/pricing';
import type { PaymentPlan } from '@lib/types';

interface PaymentPlanSelectorProps {
  plan: PaymentPlan;
  onChange: (plan: PaymentPlan) => void;
  breakdown: PriceBreakdown;
}

interface PlanCardConfig {
  value: PaymentPlan;
  labelKey: StringKey;
  icon: LucideIcon;
  main: React.ReactNode;
  sub: React.ReactNode;
}

export default function PaymentPlanSelector({
  plan,
  onChange,
  breakdown,
}: PaymentPlanSelectorProps) {
  const t = useT();

  const cards: PlanCardConfig[] = [
    {
      value: 'cash',
      labelKey: 'match.cash',
      icon: Banknote,
      main: <Money n={breakdown.cashFinal} />,
      sub: (
        <>
          {t('match.cashSavingHint')}{' '}
          <span className="num-en font-medium">
            {formatOMR(breakdown.cashDiscount, { withSymbol: false })}
          </span>{' '}
          {t('common.omr')}
        </>
      ),
    },
    {
      value: 'monthly',
      labelKey: 'match.monthly',
      icon: Calendar,
      main: (
        <>
          <Money n={breakdown.monthlyInst} decimals={2} />
          <span className="text-sm font-normal opacity-70">{t('match.perMonth')}</span>
        </>
      ),
      sub: (
        <>
          <span className="num-en font-medium">
            {formatOMR(breakdown.downPayment, { withSymbol: false })}
          </span>{' '}
          {t('match.downPayment')} · {t('match.monthlyHint')}
        </>
      ),
    },
    {
      value: 'quarterly',
      labelKey: 'match.quarterly',
      icon: CalendarDays,
      main: (
        <>
          <Money n={breakdown.quarterlyInst} decimals={2} />
          <span className="text-sm font-normal opacity-70">{t('match.perQuarter')}</span>
        </>
      ),
      sub: (
        <>
          <span className="num-en font-medium">
            {formatOMR(breakdown.downPayment, { withSymbol: false })}
          </span>{' '}
          {t('match.downPayment')} · {t('match.quarterlyHint')}
        </>
      ),
    },
    {
      value: 'flex',
      labelKey: 'match.flex',
      icon: TrendingUp,
      main: (
        <>
          <Money n={breakdown.flex.initialMonthly} decimals={0} />
          <span className="text-sm font-normal opacity-70">{t('match.perMonth')}</span>
        </>
      ),
      sub: (
        <>
          {t('match.flexMidHint')}{' '}
          <span className="num-en font-medium">
            {formatOMR(breakdown.flex.midMonthly, { withSymbol: false })}
          </span>{' '}
          {t('match.flexAfter')}
        </>
      ),
    },
  ];

  return (
    <div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {cards.map((c) => {
          const active = plan === c.value;
          const Icon = c.icon;
          return (
            <button
              key={c.value}
              type="button"
              onClick={() => onChange(c.value)}
              aria-pressed={active}
              className={`choice-card ${active ? 'choice-card-active' : ''}`}
            >
              {active && (
                <span className="absolute top-2 end-2 inline-flex items-center justify-center w-5 h-5 rounded-full bg-gold text-navy">
                  <Check className="w-3 h-3" strokeWidth={3} />
                </span>
              )}
              <Icon
                className={`w-5 h-5 ${active ? 'text-gold' : 'text-navy-400'}`}
                strokeWidth={1.75}
              />
              <span className="text-xs font-semibold uppercase tracking-wide opacity-80">
                {t(c.labelKey)}
              </span>
              <span className="text-xl md:text-2xl font-bold leading-tight flex items-baseline gap-1 flex-wrap">
                {c.main}
              </span>
              <span className={`text-xs leading-snug ${active ? 'text-white/75' : 'text-navy-500'}`}>
                {c.sub}
              </span>
            </button>
          );
        })}
      </div>

      {plan === 'flex' && <FlexSchedule breakdown={breakdown} t={t} />}
    </div>
  );
}

function Money({ n, decimals = 0 }: { n: number; decimals?: number }) {
  return (
    <span className="num-en">
      {formatOMR(n, { withSymbol: false, decimals })}
    </span>
  );
}

function FlexSchedule({
  breakdown,
  t,
}: {
  breakdown: PriceBreakdown;
  t: (k: StringKey) => string;
}) {
  const rows: Array<{ key: StringKey; valueText: React.ReactNode }> = [
    {
      key: 'match.flexScheduleM1to3',
      valueText: (
        <>
          <span className="num-en">
            {formatOMR(breakdown.flex.initialMonthly, { withSymbol: false })}
          </span>{' '}
          {t('common.omr')}
        </>
      ),
    },
    {
      key: 'match.flexScheduleM4to39',
      valueText: (
        <>
          <span className="num-en">
            {formatOMR(breakdown.flex.midMonthly, { withSymbol: false, decimals: 2 })}
          </span>{' '}
          {t('common.omr')}
        </>
      ),
    },
    {
      key: 'match.flexScheduleM40',
      valueText: (
        <>
          <span className="num-en">
            {formatOMR(breakdown.flex.settlementMonth, { withSymbol: false, decimals: 2 })}
          </span>{' '}
          {t('common.omr')}
        </>
      ),
    },
  ];

  return (
    <div className="mt-3 p-3 rounded-lg bg-navy-50 border border-navy-100 animate-slide-up">
      <div className="text-xs font-semibold text-navy-600 mb-2">
        {t('match.flexScheduleTitle')}
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-2 text-xs">
        {rows.map((r) => (
          <div key={r.key} className="flex items-baseline justify-between gap-2">
            <span className="text-navy-500">{t(r.key)}</span>
            <span className="font-semibold text-navy">{r.valueText}</span>
          </div>
        ))}
      </div>
    </div>
  );
}
